"use client";

// Client component rendering the per-transition action buttons on the
// generated detail page.
//
// The page factory (`makeDetailPage`) is a server component — it can't
// embed a JSX `<Button onClick={...}>` because Next 16's RSC runtime
// rejects function props passed across the server→client boundary. This
// wrapper lives on the client side, so it can attach onClick handlers
// directly to `Button`. The factory passes only serializable props
// (`transitions`, `version`) plus the server-action callback `onTransition`
// (server actions ARE allowed to cross the boundary).
//
// The component renders one button per transition the row's current state
// permits, plus a small `<span>` showing the pending transition name
// while the server action is in flight. We don't try to optimistic-update
// the row status here — the page action calls `revalidatePath(...)` which
// re-renders the detail page server-side after the transition lands.

import * as React from "react";
import { Button } from "@aio/platform-ui";

export interface TransitionActionsProps {
  transitions: readonly string[];
  version: number;
  onTransition: (transition: string, version: number) => void | Promise<void>;
}

export function TransitionActions({
  transitions,
  version,
  onTransition,
}: TransitionActionsProps): React.ReactElement {
  const [pending, setPending] = React.useState<string | null>(null);
  return (
    <div className="flex items-center gap-2">
      {transitions.map((name) => (
        <Button
          key={name}
          disabled={pending !== null}
          data-testid={`transition-${name}`}
          onClick={async () => {
            setPending(name);
            try {
              await onTransition(name, version);
            } finally {
              setPending(null);
            }
          }}
        >
          {pending === name ? `${name}…` : name}
        </Button>
      ))}
    </div>
  );
}
